import z from "zod";
import { AmountDto, AppliedDiscountCodeDto, BillingInformationDto, CheckoutService, CustomerInformationDto, OrderDto, PaymentMethodDto, PriceWithAdjustmentsDto, ShippingInformationDto, ShippingMethodDto, ValidationProblemDetails } from "../api";
import { AlpineValidationContext } from "../AlpineValidationContext";
import { flattenObjectPaths, getCartStore, microtask } from "../utils";
import Alpine from "alpinejs";
import { NotificationData } from "../notifier.component.types";
import { ReqiuredNonEmptyString } from "../schema-helper";
import { SyncPayload, SyncStatus, UpdateOptions } from "./order.store.types";

const customerInformation = z.object({
    firstName: ReqiuredNonEmptyString("First name is required."),
    lastName: z.nullable(z.string().optional()),
    email: z.email("Invalid email address"),
    phoneNumber: ReqiuredNonEmptyString("Telephone number is required.").min(7, "Telephone numbers should be at least 7 digits long"),
});
const minimalBillingShippingSchema =  z.object({
    firstName: ReqiuredNonEmptyString("First name is required."),
    lastName: z.nullable(z.string().optional()),
    email: z.email("Invalid email address"),
    phoneNumber: ReqiuredNonEmptyString("Telephone number is required.").min(7, "Telephone numbers should be at least 7 digits long"),
    addressLine1: ReqiuredNonEmptyString("Address line 1 is required."),
    addressLine2: z.nullable(z.string().optional()),
    city: z.nullable(z.string().optional()),
    postcode: ReqiuredNonEmptyString("Postcode is required.").transform(value=>value.replace(/\s+/g, '')).pipe(z.string().min(5, "Postcodes should be at least 5 characters long").transform(value=>value.replace(/\s+/g, ''))),
});

const orderSchemaShippingRequired = z.object({
    customerInformation: customerInformation,
    billingInformation: minimalBillingShippingSchema,
    shippingInformation: minimalBillingShippingSchema
});
const orderSchemaShippingOptional = z.object({
    customerInformation: customerInformation,
    billingInformation: minimalBillingShippingSchema,
});

let customerInformationValidator! : AlpineValidationContext<OrderStore, typeof orderSchemaShippingRequired | typeof orderSchemaShippingOptional, any, any>;

export class OrderStore {
    public storeId: string;

    // Public State
    public currencyCode: string = "gbp";
    public allowedShippingMethods: ShippingMethodDto[] = [];
    public allowedPaymentMethods: PaymentMethodDto[] = [];

    public customerInformation!: CustomerInformationDto;
    public billingInformation!: BillingInformationDto;
    public shippingInformation!: ShippingInformationDto;
    public notes: string | null | undefined ;

    public discountCodes: AppliedDiscountCodeDto[] = [];

    public shippingMethodId: string | undefined | null = null;
    public selectedShippingMethod: ShippingMethodDto | null | undefined = null;
    public paymentMethodId: string | undefined | null = null;
    public selectedPaymentMethod: PaymentMethodDto | null | undefined = null;

    public shippingTotalPrice: PriceWithAdjustmentsDto | null | undefined = null;

    public totalPrice: PriceWithAdjustmentsDto | null | undefined = null;

    public transactionAmount: AmountDto | null | undefined = null;

    public schemaErrors: Record<string, string[]> = {};
    public loading = false;
    public pendingShippingOptionUpdate = false;
    public isStateValid = false;

    // Status
    private loadingTimeoutHandle: number | null | undefined;
    private status: SyncStatus = 'idle';
    private queue: SyncPayload = {};
    private debounceTimer?: number;
    private config = {
        debounceInterval: 600,
        highPriorityKeys: new Set<keyof SyncPayload>
    };

    constructor(storeId: string, bootstrapData: OrderDto) {
        this.storeId = storeId;
        customerInformationValidator = new AlpineValidationContext<OrderStore, typeof orderSchemaShippingRequired | typeof orderSchemaShippingOptional, any, any>(orderSchemaShippingOptional, this);
        this.applyServerState(bootstrapData);
    }

    init() {
        console.debug("OrderStore initialized");
        Alpine.effect( async () => {
            const { shippingMethodId: _shippingMethodId } = this;
            console.debug(`Shipping method has been set`);
            await microtask();
            this.updateSelectedShippingMethod();
        });
        Alpine.effect( async () => {
            const { paymentMethodId: _paymentMethodId } = this;
            console.debug(`Payment method has been set`);
            await microtask();
            this.updateSelectedPaymentMethod();
        });
        Alpine.effect(()=>{
            if(this.shippingInformation.isSameAsBilling) {
                customerInformationValidator = new AlpineValidationContext<OrderStore, typeof orderSchemaShippingRequired | typeof orderSchemaShippingOptional, any, any>(orderSchemaShippingOptional, this);
            } else {
                customerInformationValidator = new AlpineValidationContext<OrderStore, typeof orderSchemaShippingRequired | typeof orderSchemaShippingOptional, any, any>(orderSchemaShippingRequired, this);
            }
        });
        Alpine.effect(()=>{
            const { shippingInformation: _shippingInformation, billingInformation: _billingInformation, customerInformation: _customerInformation , selectedShippingMethod: _selectedShippingMethod, selectedPaymentMethod: _selectedPaymentMethod } = this;
            console.debug("Customer information, shipping information, or billing information has been set, validating...");
            const { outcome } = customerInformationValidator.validate(true);
            this.isStateValid = outcome ?? false;
        });
    }


    public async updateCustomerInformation(partial: SyncPayload, options: UpdateOptions = {})
    {
        console.debug("Queueing customer information update:", partial, options);
        const { policy = 'debounce', priority = 'normal', validate = true, throwOnInvalid, } = options;

        if(partial.customerInformation) {
            this.customerInformation = { ...this.customerInformation, ...partial.customerInformation };
            this.billingInformation = { ...this.billingInformation, ...partial.customerInformation };
            this.queue.customerInformation = { ...this.queue.customerInformation ?? this.customerInformation, ...partial.customerInformation };
            this.queue.billingInformation = { ...this.queue.billingInformation ?? this.billingInformation, ...partial.customerInformation };
        }
        // Important: Update billing information before shipping information to ensure "same as billing" works correctly
        if(partial.billingInformation) {
            this.billingInformation = { ...this.billingInformation, ...partial.billingInformation };
            this.queue.billingInformation = { ...this.queue.billingInformation ?? this.billingInformation, ...partial.billingInformation };
        }
        if(partial.shippingInformation) {
            const shippingSameAsBilling = partial.shippingInformation.isSameAsBilling
            if(shippingSameAsBilling !== undefined) {
                this.shippingInformation.isSameAsBilling = shippingSameAsBilling;
                this.queue.shippingInformation = { isSameAsBilling: shippingSameAsBilling };
            } else if( Object.keys(partial.shippingInformation).length > 0 ) {
                // If there are other fields to update besides isSameAsBilling
                this.shippingInformation = { ...this.shippingInformation, ...partial.shippingInformation };
                this.queue.shippingInformation = { ...this.queue.shippingInformation, ...partial.shippingInformation };
            }
        }
        if(partial.notes !== undefined) {
            this.notes = partial.notes;
            this.queue.notes = partial.notes;
        }

        if(validate) {
            const { errors, successes: _successes, outcome } = customerInformationValidator.validate(true);
            if(!outcome) {
                // Validation failed, do not proceed with update
                console.debug("Validation failed, not updating customer information:", errors);
                if(throwOnInvalid) {
                    throw new Error("Validation failed for customer information update.");
                }
                return;
            }
        }

        const hasHighPriorityKey = priority === 'high' || Object.keys(partial).some(key => this.config.highPriorityKeys.has(key as keyof SyncPayload));
        if(policy === 'immediate' || hasHighPriorityKey) {
            await this.flushUpdatesNow();
        } else if(policy === 'blur') {
            this.flushUpdatesSoon();
        } else if(policy === 'idle') {
            this.flushUpdatesOnIdle();
        } else {
            this.flushUpdatesDebounced();
        }
    }
    public async flushUpdatesNow() {
        if(this.debounceTimer) {
            window.clearTimeout(this.debounceTimer);
        }
        await this.flushUpdates();
    }
    public flushUpdatesSoon() {
        if(this.status === 'syncing')
            return;
        this.status = 'scheduled';
        if(this.debounceTimer)
            window.clearTimeout(this.debounceTimer);
        this.debounceTimer = window.setTimeout(() => {
            this.flushUpdates();
        }, 0);
    }
    public flushUpdatesOnIdle() {
        if(this.status === 'syncing')
            return;
        this.status = 'scheduled';
        if('requestIdleCallback' in window) {
            window.requestIdleCallback(() => void this.flushUpdates(), { timeout: this.config.debounceInterval });
        } else {
            this.flushUpdatesDebounced();
        }
    }
    public flushUpdatesDebounced() {
        if(this.status === 'syncing')
            return;
        this.status = 'scheduled';
        if(this.debounceTimer)
            window.clearTimeout(this.debounceTimer);
        this.debounceTimer = window.setTimeout(() => {
            this.flushUpdates();
        }, this.config.debounceInterval);
    }
    private async flushUpdates() {
        if(this.status === 'syncing') return;
        const hasQueue = !!this.queue.customerInformation || !!this.queue.shippingInformation || !!this.queue.billingInformation || this.queue.notes !== undefined;
        if(!hasQueue) {
            this.status = 'idle';
            return;
        }

        this.status = 'syncing';
        try {
            await this.updateOrderInformation();
            this.queue = {};
            this.status = 'idle';
        }
        catch (error) {
            console.error("Error flushing updates:", error);
            this.status = 'error';
        }
    }

    private applyServerState(state: OrderDto) {
        this.customerInformation = state.customerInformation;
        this.shippingInformation = state.shippingInformation;
        this.billingInformation = state.billingInformation;
        this.notes = state.notes;

        this.currencyCode = state.currencyCode;
        this.allowedShippingMethods = state.availableShippingMethods ?? [];
        this.pendingShippingOptionUpdate = false;
        this.allowedPaymentMethods = state.availablePaymentMethods ?? [];

        this.discountCodes = state.discountCodes ?? [];

        this.shippingMethodId = state.selectedShippingMethod?.id ?? null;
        this.selectedShippingMethod = state.selectedShippingMethod ?? null;

        this.paymentMethodId = state.selectedPaymentMethod?.id ?? null;
        this.selectedPaymentMethod = state.selectedPaymentMethod ?? null;

        this.shippingTotalPrice = state.shippingTotalPrice ?? null;

        this.totalPrice = state.totalPrice ?? null;

        // We gate this to avoid unnecessary updates with stripe
        if(this.transactionAmount?.value !== state.transactionAmount?.value) {
            this.transactionAmount = state.transactionAmount ?? null;
        }
    }

    public async applyDiscountCode(discountCode: string) : Promise<{ success: boolean, error: string } | { success: true }> {
        if(discountCode.trim() === "") {
            return { success: false, error: "Please enter a discount code." };
        }

        this.beginLoading();

        const { data, error } = await CheckoutService.postApplyDiscountOrGiftCardCode({
            path: {
                storeId: this.storeId,
            },
            body: {
                code: discountCode.trim(),
            }
        });

        this.endLoading();

        if(error){
            return { success: false, error: error.errors["code"]?.join(", ") || "An error occurred applying the discount code." };
        }

        if(data){
            getCartStore().RefreshCart();
            this.applyServerState(data);
        }
        return { success: true };
    }
    public async removeDiscountCode(code: AppliedDiscountCodeDto) : Promise<{ success: boolean, error: string } | { success: true }>{
        this.beginLoading();
        const { data, error } = await CheckoutService.postRemoveDiscountOrGiftCardCode({
            path: {
                storeId: this.storeId,
            },
            body: {
                code: code.code,
            }
        });

        this.endLoading();

        if(error){
            return { success: false, error: error.errors[""]?.join(", ") || "An error occurred applying the discount code." };
        }

        if(data){
            getCartStore().RefreshCart();
            this.applyServerState(data);
        }
        return { success: true };
    }

    public async updateOrderInformation() {
        this.beginLoading();
        console.debug("Updating customer information with:", this.queue);
        const { data, error } = await CheckoutService.postUpdateCustomerInformation({
            path: {
                storeId: this.storeId,
            },
            body: {
                customerInformation: this.queue.customerInformation,
                shippingInformation: this.queue.shippingInformation,
                billingInformation: this.queue.billingInformation,
                notes: this.queue.notes,
            }
        })

        if(error) {
            console.error("Error updating customer information:", error);
            //window.appInsights?.trackTrace({ message: "Error updating customer information on server", severityLevel: 3, properties: { error: JSON.stringify(error) } });
            window.dispatchEvent(new CustomEvent<NotificationData>("notify", {
                detail: {
                    type: 'error',
                    message: this.getErrorMessage(error) ?? "There was an error updating customer information.",
                }
            }));

            throw new Error(`Error updating customer information( Status:${error.status} - ${error.title}, Detail: ${error.detail}, Errors: ${JSON.stringify(error.errors)})`);
        }

        if(data){
            this.applyServerState(data);
        }
        this.endLoading();

        return error;
    }

    public async updateSelectedShippingMethod() {
        if(!this.shippingMethodId) {
            console.debug("No shipping method selected, skipping update.");
            return;
        }
        if(this.shippingMethodId === this.selectedShippingMethod?.id) {
            console.debug("Selected shipping method is unchanged, not updating.");
            return;
        } else {
            console.debug("Selected shipping method has changed, updating.");
        }

        console.debug(`Setting order shipping method to ${this.shippingMethodId}`);
        this.beginLoading();
        const { data, error } = await CheckoutService.postSetShippingMethod({
            path: {
                storeId: this.storeId
            },
            body: {
                shippingMethodId: this.shippingMethodId
            }
        });

        if(error) {
            console.error("Error updating shipping method:", error);
            window.appInsights?.trackTrace({ message: "Error setting selected shipping method on server", severityLevel: 3, properties: { error: JSON.stringify(error) } });
            window.dispatchEvent(new CustomEvent<NotificationData>("notify", {
                detail: {
                    type: 'error',
                    message: this.getErrorMessage(error) ?? "There was an error setting the shipping method.",
                }
            }));
        }

        if(data) {
            this.applyServerState(data);
        }

        this.endLoading();
    }

    public async updateSelectedPaymentMethod() {
        if(!this.paymentMethodId) {
            console.debug("No payment method selected, skipping update.");
            return;
        }
        if(this.paymentMethodId === this.selectedPaymentMethod?.id) {
            console.debug("Selected payment method is unchanged, not updating.");
            return;
        } else {
            console.debug("Selected payment method has changed, updating.");
        }

        console.debug(`Setting order payment method to ${this.paymentMethodId}`);
        this.beginLoading();
        const { data, error } = await CheckoutService.postSetPaymentMethod({
            path: {
                storeId: this.storeId
            },
            body: {
                paymentMethodId: this.paymentMethodId
            }
        });

        if(error) {
            console.error("Error updating payment method:", error);
            window.appInsights?.trackTrace({ message: "Error setting selected payment method on server", severityLevel: 3, properties: { error: JSON.stringify(error) } });
            window.dispatchEvent(new CustomEvent<NotificationData>("notify", {
                detail: {
                    type: 'error',
                    message: this.getErrorMessage(error) ?? "There was an error setting the payment method.",
                }
            }));
        }

        if(data) {
            this.applyServerState(data);
        }

        this.endLoading();
    }

    private beginLoading() {
        if(this.loadingTimeoutHandle) {
            clearTimeout(this.loadingTimeoutHandle);
        }
        this.loading = true;
    }
    private endLoading() {
        this.loadingTimeoutHandle = window.setTimeout(() => {
            this.loading = false;
            this.pendingShippingOptionUpdate = false;
            this.loadingTimeoutHandle = undefined;
        }, 250);
    }
    private getErrorMessage(error?: ValidationProblemDetails): string | null {
        if(!error || !error.errors){
            return null;
        }
        let message = "";
        for (const errors of Object.values(error.errors)) {
            message += errors.join(", ") + " ";
        }
        return message.trim();
    }

    public getValidatableFields() : string[] {
        return flattenObjectPaths(customerInformationValidator.schema);
    }

    public validateField(field: string) : boolean {
        const result = customerInformationValidator.validateOnly(field);

        this.schemaErrors = { ...customerInformationValidator.formState.errors };

        return result;
    }
    public isValid(field: string) : boolean {
        return !this.schemaErrors[field];
    }
    public isInvalid(field: string) : boolean {
        if(this.schemaErrors[field]){
            return true;
        }
        return false;
    }
    public validateAll(silent: boolean = false) : boolean {
        const { outcome } = customerInformationValidator.validate(silent);
        return outcome ?? false;
    }

    public firstErrorFor(field: string) : string | null {
        if(this.schemaErrors[field] && this.schemaErrors[field].length > 0) {
            return this.schemaErrors[field][0];
        }
        return null;
    }
}